import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChildren,
} from '@angular/core';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { DialogConfirmData } from '../dialog-confirm/dialog-confirm-data';
import { DialogConfirmComponent } from '../dialog-confirm/dialog-confirm.component';
import { EditableDataTableColumn } from './models/editable-data-table-column';
import { EditableDataTableColumnFilter } from './models/editable-data-table-column-filter';
import { EditableDataTableColumnType } from './models/editable-data-table-column-type';
import { EditableDataTableModel } from './models/editable-data-table-model';
import { EditableDataTableState } from './models/editable-data-table-state';

import { SelectionModel } from '@angular/cdk/collections';

@Component({
  selector: 'app-editable-data-table',
  standalone: false,
  styleUrls: ['./editable-data-table.component.css'],
  templateUrl: './editable-data-table.component.html',
})
export class EditableDataTableComponent implements OnInit, OnChanges {
  displayedColumns: string[] = [];
  elementeState = EditableDataTableState;
  columnType = EditableDataTableColumnType;
  filterOption = EditableDataTableColumnFilter;
  dataMinima: Date;
  dataMaxima: Date;
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  private lastHeight: string;
  @Input('columns') columns: Array<EditableDataTableColumn> =
    new Array<EditableDataTableColumn>();
  @Input('itens') itens: Array<EditableDataTableModel> =
    new Array<EditableDataTableModel>();
  @Input('tableId') tableId: string = `tableId-${new Date()
    .getTime()
    .toString()}`;
  @Input('deleteInfoLabel') deleteInfoLabel: string;
  @Input('deleteInfoContent') deleteInfoContent: string;
  @Output('autocompleteSelected')
  autocompleteSelectedEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output('onDelete') onDelete: EventEmitter<any> = new EventEmitter<any>();
  @Output('onSelected') onSelected: EventEmitter<any> = new EventEmitter<any>();
  @Output('onSave') onSave: EventEmitter<any> = new EventEmitter<any>();
  @ContentChild('customColumnRef1', { static: false })
  customColumnRef1: TemplateRef<any>;
  @ViewChildren(MatAutocompleteTrigger)
  autoCompletesTriggers: Array<MatAutocompleteTrigger>;

  selection = new SelectionModel<any>(true, []);

  constructor(
    private dialog: MatDialog,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const minDate = new Date();

    minDate.setDate(minDate.getDate() - 1);
    minDate.setMonth(minDate.getMonth() - 18);

    // this.configDatePicker.minYear = minDate.getFullYear() - 10;
    // this.configDatePicker.maxYear = minDate.getFullYear() + 10;
    // this.configDatePicker.minDate = minDate;
    // this.configDatePicker.maxDate = new Date();

    this.dataMinima = minDate;
    this.dataMaxima = new Date();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.displayedColumns = this.columns.map((c) => c.id);
    this.dataSource.data = this.itens;
  }

  public changeState(state: EditableDataTableState): void {
    this.dataSource.data.forEach((element) => {
      element.state = state;
    });
  }

  private deleteItem(item: any) {
    // item.state = this.elementeState.VIEW;
    let modal = this.dialog.open(DialogConfirmComponent, {
      data: {
        title: 'Excluir',
        text: 'Você deseja realmente excluir este relatório?',
        noText: 'Cancelar',
        yesText: 'Excluir',
        titleIcon: '',
        extraInfoTitle: 'Verifique as informações antes de excluir:',
        extraInfoLabel: this.deleteInfoLabel,
        extraInfoContent: item[this.deleteInfoContent],
      } as DialogConfirmData,
    });

    modal
      .afterClosed()
      .toPromise()
      .then((yes) => {
        this.onDelete.emit({ deleted: !!yes, item: item });
        return !!yes;
      });
  }

  private editItem(item: any) {
    item.state = this.elementeState.EDIT;
  }

  private cancelEditItem(item: any) {
    item.state = this.elementeState.VIEW;
  }

  private saveItem(item: any) {
    item.state = this.elementeState.VIEW;
    this.onSave.emit(item);
  }

  private changeDate(
    newDate: Date,
    column: EditableDataTableColumn,
    element: EditableDataTableModel
  ) {
    element[column.id] = newDate;
    this.isValid(column, element);
  }

  private autocompleteSelectedValue(
    value: any,
    column: EditableDataTableColumn,
    element: EditableDataTableModel
  ) {
    this.autocompleteSelectedEmitter.emit({
      value: value,
      column: column,
      element: element,
    });
    element[column.id] = value;
    element[column.params.displayText] = this.getAutoCompleteTextByValue(
      value,
      column
    );
    this.isValid(column, element);
    this.changeDetector.detectChanges();
  }

  private getAutoCompleteTextByValue(
    value: any,
    column: EditableDataTableColumn
  ): string {
    const _value = column.params.options.find((a) => a[column.id] == value);

    if (_value == undefined) return '';

    return _value[column.params.displayText];
  }

  public validate(): boolean {
    const invalidItems = [];
    this.itens.forEach((element) => {
      this.columns.forEach((column) => {
        const isValid = this.isValid(column, element);

        if (!isValid)
          invalidItems.push({
            column: column,
            element: element,
          });
      });
    });

    return invalidItems.length == 0;
  }

  isValid(column: EditableDataTableColumn, element: EditableDataTableModel) {
    if (column.isValid) {
      if (!element.invalidColumns) element.invalidColumns = [];
      const isValid = column.isValid(element, column);

      if (isValid) delete element.invalidColumns[column.id];
      else element.invalidColumns[column.id] = true;

      return isValid;
    }

    return true;
  }

  /* Events - EditableDataTableColumn - Checkbox */

  selectItem(row: any): void {
    this.selection.toggle(row);
    this.emitSelected();
  }

  /** aria-label do checkbox **/
  checkboxLabel(row?: any): string {
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
      row.position + 1
    }`;
  }

  private emitSelected(): void {
    this.onSelected.emit(this.selection.selected);
  }

  clearSelection(): void {
    this.selection.clear();
  }

  /* ****** */
}
